import React, { useState, useEffect, useMemo } from 'react';
import ToolCard from '../components/tools/ToolCard';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const Tools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const fetchTools = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('ai_tools')
          .select('*')
          .eq('status', 'published')
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (data) {
          setTools(data.map(item => ({
            id: item.id,
            name: item.name,
            category: item.category || 'General',
            hook: item.hook,
            description: item.description,
            bestFor: item.best_for,
            features: item.features,
            pricing: item.pricing,
            isAffiliate: item.is_affiliate,
            logo: item.logo,
            badges: item.badges,
            deepLinks: item.deep_links,
            ctaText: item.cta_text,
            ctaUrl: item.cta_url
          })));
        }
      } catch (err) {
        console.error("Error fetching tools:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, []);

  // Dynamically extract categories handling comma-separated values
  const categories = useMemo(() => {
    const allCats = tools
      .flatMap(t => (t.category || 'General').split(',').map(c => c.trim()))
      .filter(Boolean);
    return [...new Set(allCats)].sort();
  }, [tools]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const filteredTools = useMemo(() => {
    return tools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(search.toLowerCase()) || 
                            (tool.description && tool.description.toLowerCase().includes(search.toLowerCase()));
      
      const toolCats = (tool.category || 'General').split(',').map(c => c.trim());
      const matchesCategory = selectedCategories.length === 0 || 
                              selectedCategories.some(sc => toolCats.includes(sc));
      
      return matchesSearch && matchesCategory;
    });
  }, [tools, search, selectedCategories]);

  return (
    <div className="w-full">
      {/* Header & Search */}
      <div className="bg-brand-surface border-b border-brand-border pt-10 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
            <div>
              <h1 className="text-[32px] md:text-[36px] lg:text-[40px] font-bold text-brand-text mb-4 leading-tight">AI Tools Directory</h1>
              <p className="text-[18px] md:text-[20px] text-brand-secondary max-w-[700px] leading-[1.55]">
                Hand-curated, tested, and reviewed. Discover the most powerful new AI tools to accelerate your workflow.
              </p>
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button 
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`flex items-center justify-center gap-2 px-5 py-3 rounded-[8px] text-[16px] font-bold transition-colors border shrink-0 ${
                  isFilterOpen || selectedCategories.length > 0
                    ? 'bg-brand-mint/10 text-brand-mint border-brand-mint/30'
                    : 'bg-brand-bg text-brand-text border-brand-border hover:bg-brand-raised'
                }`}
              >
                <SafeIcon icon="Filter" className="w-5 h-5" />
                <span>{selectedCategories.length > 0 ? `Filters (${selectedCategories.length})` : 'Filter'}</span>
                <SafeIcon icon={isFilterOpen ? "ChevronUp" : "ChevronDown"} className="w-5 h-5 ml-1 hidden sm:block" />
              </button>
              
              <div className="relative w-full md:w-[320px]">
                <SafeIcon icon="Search" className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-muted" />
                <input 
                  type="text" 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tools..." 
                  className="w-full bg-brand-bg border border-brand-border rounded-[8px] pl-12 pr-4 py-3 text-[16px] text-brand-text focus:outline-none focus:border-brand-mint transition-colors placeholder-brand-muted"
                />
              </div>
            </div>
          </div>

          {/* Collapsible Category Panel */}
          {isFilterOpen && (
            <div className="pt-5 pb-3 border-t border-brand-border animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-[13px] md:text-[14px] font-mono font-bold text-brand-muted uppercase tracking-widest mb-4">Select Categories</p>
              {categories.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {categories.map(cat => (
                    <label 
                      key={cat}
                      className="flex items-center gap-2.5 cursor-pointer group px-4 py-2.5 rounded-[8px] hover:bg-brand-bg border border-transparent hover:border-brand-border transition-colors select-none"
                    >
                      <input 
                        type="checkbox"
                        className="sr-only"
                        checked={selectedCategories.includes(cat)}
                        onChange={() => toggleCategory(cat)}
                      />
                      <div className={`w-5 h-5 rounded-[4px] border flex items-center justify-center transition-colors ${
                        selectedCategories.includes(cat) 
                          ? 'bg-brand-mint border-brand-mint' 
                          : 'border-brand-muted group-hover:border-brand-text'
                      }`}>
                        {selectedCategories.includes(cat) && <SafeIcon icon="Check" className="w-3.5 h-3.5 text-brand-bg" />}
                      </div>
                      <span className="text-[16px] md:text-[18px] text-brand-secondary group-hover:text-brand-text transition-colors">{cat}</span>
                    </label>
                  ))}
                  {selectedCategories.length > 0 && (
                    <button 
                      onClick={() => setSelectedCategories([])}
                      className="px-4 py-2.5 text-[15px] font-bold text-brand-breaking hover:opacity-80 transition-opacity ml-2"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-[16px] text-brand-secondary italic">No categories available yet.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <SafeIcon icon="Loader" className="w-10 h-10 text-brand-mint animate-spin" />
          </div>
        ) : filteredTools.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredTools.map(tool => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <h3 className="text-[20px] md:text-[24px] font-bold text-brand-text mb-3">No Tools Found</h3>
            <p className="text-brand-secondary text-[18px] md:text-[20px]">Try adjusting your search or category filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tools;